import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { listAccounts, portfolioSummary } from '../../lib/api/endpoints';
import { marketHoursRefetchInterval } from '../../lib/queryRefresh';
import { useUiStore, type RememberedAccount } from '../../stores/uiStore';

export const accountKeys = {
  all: ['accounts'] as const,
  portfolioSummary: (accountId: string | null) =>
    [...accountKeys.all, 'portfolio-summary', accountId] as const,
};

export function useAccounts() {
  return useQuery({
    queryKey: accountKeys.all,
    queryFn: listAccounts,
  });
}

/** Starts the accounts request alongside the session check instead of after it. */
export function usePrefetchAccounts(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    void queryClient.prefetchQuery({ queryKey: accountKeys.all, queryFn: listAccounts });
  }, [queryClient]);
}

/**
 * The first account, falling back to the one remembered from the last visit while
 * /accounts loads so account-scoped queries can start immediately.
 */
export function usePrimaryAccount() {
  const accounts = useAccounts();
  const remembered = useUiStore((state) => state.lastPrimaryAccount);
  const setLastPrimaryAccount = useUiStore((state) => state.setLastPrimaryAccount);
  const loaded = useMemo((): RememberedAccount | null => {
    const first = accounts.data?.[0];
    if (!first) return null;
    return {
      id: first.id,
      name: first.name,
      broker: first.broker,
      currency: first.currency,
      mandate: first.mandate,
    };
  }, [accounts.data]);

  useEffect(() => {
    if (accounts.data) setLastPrimaryAccount(loaded);
  }, [accounts.data, loaded, setLastPrimaryAccount]);

  return { accounts, account: accounts.data ? loaded : remembered };
}

export function usePortfolioSummary(accountId: string | null) {
  return useQuery({
    queryKey: accountKeys.portfolioSummary(accountId),
    queryFn: () => portfolioSummary(accountId ?? ''),
    enabled: Boolean(accountId),
    refetchInterval: marketHoursRefetchInterval,
  });
}
